import { useState, useMemo, useCallback, memo } from 'react';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Icon,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { Plus, Pencil, Calendar as CalendarIcon } from 'lucide-react';
import { ActionItem, User } from '../../types';
import { AddActionItemForm } from './AddActionItemForm';
import { EditActionItemForm } from './EditActionItemForm';

interface ActionItemsSectionProps {
  actionItems: ActionItem[];
  rockId: string;
  users: User[];
  ownerName: string;
  addActionItem?: (rockId: string, task: string, assigneeId: string | null, dueDate: string) => Promise<void>;
  updateActionItem?: (actionItemId: string, task: string, assigneeId: string | null, dueDate: string) => Promise<void>;
  toggleActionItem?: (actionItemId: string, complete: boolean) => Promise<void>;
  onRefresh: () => Promise<void>;
}

const ActionItemsSectionComponent = ({
  actionItems,
  rockId,
  users,
  ownerName,
  addActionItem,
  updateActionItem,
  toggleActionItem,
  onRefresh,
}: ActionItemsSectionProps) => {
  const [showActionItemForm, setShowActionItemForm] = useState(false);
  const [editingActionItemId, setEditingActionItemId] = useState<string | null>(null);
  const toast = useToast();

  const actionItemBg = useColorModeValue('green.50', 'green.900');
  const actionItemBorder = useColorModeValue('green.200', 'green.600');
  const actionItemText = useColorModeValue('green.700', 'green.300');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const textColorLight = useColorModeValue('gray.500', 'gray.400');
  const textColorLighter = useColorModeValue('gray.600', 'gray.500');

  // Memoize action items with assignee and owner information to prevent unnecessary recalculations
  const actionItemsWithAssignees = useMemo(() => {
    return actionItems.map((item) => {
      const assigneeId = item.assignedToUserId || item.assigneeId;
      const ownerId = item.ownerId;
      return {
        ...item,
        assignee: assigneeId ? users.find((u) => u.id === assigneeId) : undefined,
        owner: ownerId ? users.find((u) => u.id === ownerId) : undefined,
      };
    });
  }, [actionItems, users]);

  // Split action items into incomplete and completed, sorted by date
  const incompleteActionItems = useMemo(() => {
    return actionItemsWithAssignees
      .filter((item) => !item.complete)
      .sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateA - dateB;
      });
  }, [actionItemsWithAssignees]);

  const completedActionItems = useMemo(() => {
    return actionItemsWithAssignees
      .filter((item) => item.complete)
      .sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateA - dateB;
      });
  }, [actionItemsWithAssignees]);

  const handleToggleActionItemForm = useCallback(() => {
    setShowActionItemForm((prev) => !prev);
  }, []);

  const handleActionItemAdded = useCallback(async () => {
    try {
      await onRefresh();
      setShowActionItemForm(false);
    } catch (error) {
      console.error('Error refreshing action items after add:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh action items',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [onRefresh, toast]);

  const handleActionItemUpdated = useCallback(async () => {
    await onRefresh();
    setEditingActionItemId(null);
  }, [onRefresh]);

  const handleToggleActionItem = async (actionItemId: string, complete: boolean) => {
    if (!toggleActionItem) return;
    try {
      await toggleActionItem(actionItemId, complete);
      await onRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update action item',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box bg={actionItemBg} border="1px" borderColor={actionItemBorder} borderRadius="lg" p={4}>
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={2}>
          <CheckCircleIcon color="green.500" />
          <Text fontWeight="medium" fontSize="sm" color={actionItemText}>
            Action Items
          </Text>
        </HStack>
        {addActionItem && !showActionItemForm && (
          <Button
            size="xs"
            colorScheme="green"
            leftIcon={<Icon as={Plus} w={3} h={3} />}
            onClick={handleToggleActionItemForm}
          >
            Add Action Item
          </Button>
        )}
      </Flex>

      {addActionItem && showActionItemForm && (
        <AddActionItemForm
          rockId={rockId}
          onAdd={(task: string, assigneeId: string | null, dueDate: string) =>
            addActionItem(rockId, task, assigneeId, dueDate)
          }
          onSuccess={handleActionItemAdded}
          onCancel={() => setShowActionItemForm(false)}
          users={users}
        />
      )}

      {actionItemsWithAssignees.length === 0 && !showActionItemForm && (
        <Text fontSize="sm" color={textColorLight} textAlign="left" py={2}>
          No action items yet
        </Text>
      )}

      {actionItemsWithAssignees.length > 0 && (
        <>
          <VStack align="stretch" spacing={2}>
            {incompleteActionItems.length === 0 && completedActionItems.length > 0 && (
              <Text fontSize="sm" color={textColorLight} textAlign="left" py={2}>
                All action items are completed
              </Text>
            )}
            {incompleteActionItems.map((item) => (
              <Box key={item.id}>
                {editingActionItemId === item.id ? (
                  <EditActionItemForm
                    actionItem={item}
                    rockId={rockId}
                    onUpdate={updateActionItem!}
                    onSuccess={handleActionItemUpdated}
                    onCancel={() => setEditingActionItemId(null)}
                    users={users}
                  />
                ) : (
                  <HStack spacing={2} justify="space-between">
                    <HStack spacing={2}>
                      <Checkbox
                        isChecked={item.complete}
                        onChange={(e) => handleToggleActionItem(item.id, e.target.checked)}
                        colorScheme="green"
                        isDisabled={!toggleActionItem}
                      />
                      {updateActionItem && (
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setEditingActionItemId(item.id)}
                          aria-label="Edit action item"
                        >
                          <Icon as={Pencil} w={3} h={3} />
                        </Button>
                      )}
                      <Text
                        fontSize="sm"
                        textDecoration={item.complete ? 'line-through' : 'none'}
                        color={item.complete ? textColorLight :  textColor}
                        flex={1}
                      >
                        {item.description || item.task || 'Untitled action item'}
                      </Text>
                    </HStack>
                    <HStack spacing={2} fontSize="xs" color={textColorLighter}>
                      {item.owner && <Text>Owner: {item.owner.name}</Text>}
                      {item.assignee && <Text>Assigned: {item.assignee.name}</Text>}
                      {!item.owner && !item.assignee && <Text>Unassigned</Text>}
                      {item.dueDate && (
                        <>
                          <Text>•</Text>
                          <HStack spacing={1}>
                             <Icon as={CalendarIcon} w={3} h={3} />
                             <Text>
                               {item.dueDate instanceof Date 
                                 ? item.dueDate.toLocaleDateString() 
                                 : new Date(item.dueDate).toLocaleDateString()}
                             </Text>
                          </HStack>
                        </>
                      )}
                    </HStack>
                  </HStack>
                )}
              </Box>
            ))}
          </VStack>

          {completedActionItems.length > 0 && (
            <Accordion allowToggle size="sm" mt={2}>
              <AccordionItem border="none">
                <h2>
                  <AccordionButton _expanded={{ bg: 'green.100' }} borderRadius="md" px={2} py={1}>
                    <Box flex="1" textAlign="left" fontSize="sm" fontWeight="medium" color={actionItemText}>
                      Completed ({completedActionItems.length})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={2} mt={2}>
                    {completedActionItems.map((item) => (
                      <HStack key={item.id} spacing={2} justify="space-between" opacity={0.6}>
                        <HStack spacing={2}>
                          <Checkbox
                            isChecked={item.complete}
                            onChange={(e) => handleToggleActionItem(item.id, e.target.checked)}
                            colorScheme="green"
                            isDisabled={!toggleActionItem}
                          />
                          <Text
                            fontSize="sm"
                            textDecoration="line-through"
                            color={textColorLight}
                            flex={1}
                          >
                            {item.description || item.task || 'Untitled action item'}
                          </Text>
                        </HStack>
                        <HStack spacing={2} fontSize="xs" color={textColorLighter}>
                          {item.owner && <Text>Owner: {item.owner.name}</Text>}
                          {item.assignee && <Text>Assigned: {item.assignee.name}</Text>}
                          {!item.owner && !item.assignee && <Text>Unassigned</Text>}
                          {item.dueDate && (
                            <>
                              <Text>•</Text>
                              <HStack spacing={1}>
                                 <Icon as={CalendarIcon} w={3} h={3} />
                                 <Text>
                                   {item.dueDate instanceof Date 
                                     ? item.dueDate.toLocaleDateString() 
                                     : new Date(item.dueDate).toLocaleDateString()}
                                 </Text>
                              </HStack>
                            </>
                          )}
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
};

export const ActionItemsSection = memo(ActionItemsSectionComponent);
